/**
 * useVulnerabilityData Hook
 *
 * Lightweight wrapper around useGetRisks that provides vulnerability-specific
 * data fetching and transformation logic. This hook serves as the data layer
 * for the vulnerabilities section.
 *
 * Primary responsibilities:
 * - Fetch vulnerability data with filters
 * - Handle pagination
 * - Transform source display text
 * - Provide empty state detection
 *
 * Note: No frontend filtering is applied. The backend handles all filtering
 * and the frontend simply displays what the API returns (SRP).
 */

import React, { useMemo } from 'react';

import { AllIntegrations } from '@/hooks/useIntegration';
import { Filter, RiskWithVulnerability, useGetRisks } from '@/hooks/useRisks';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { getDisplaySourceText } from '@/sections/vulnerabilities/utils/sourceDisplay.util';
import { useAuth } from '@/state/auth';
import type { DropdownOption } from '@/types/dropdown.types';
import type { VulnerabilityStatusMode } from '@/utils/vulnerabilityStatus.util';

/**
 * Hook parameters
 */
export interface UseVulnerabilityDataParams {
  /** Parent filters to merge with vulnerability filters */
  parentFilters?: Filter;
  /** Source filter options for display text transformation */
  sourceFilterOptions?: Array<
    DropdownOption & { isGroup?: boolean; items?: DropdownOption[] }
  >;
  /** Currently visible column IDs — forwarded to useGetRisks to decide whether to fetch vulnerability data */
  activeColumns?: string[];
}

/**
 * Hook return value
 */
export interface UseVulnerabilityDataReturn {
  /** Vulnerability data with transformed source display */
  data: RiskWithVulnerability[];
  /** Vulnerability data filtered by current mode (legacy hides Triaged, new shows all) */
  filteredData: RiskWithVulnerability[];
  /** Current vulnerability status mode ('legacy' | 'new') */
  mode: VulnerabilityStatusMode;
  /** Whether mode settings are still loading */
  isModeLoading: boolean;
  /** Loading error if any */
  error: Error | null;
  /** Query status */
  status: 'pending' | 'error' | 'success';
  /** Fetch next page */
  fetchNextPage: () => Promise<unknown>;
  /** Whether data is currently being fetched (including filter changes) */
  isFetching: boolean;
  /** Whether next page is being fetched */
  isFetchingNextPage: boolean;
  /** Whether there are more pages */
  hasNextPage: boolean | undefined;
  /** Refetch data */
  refetch: () => Promise<unknown>;
  /** Selected rows state */
  selectedRows: string[];
  /** Set selected rows */
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  /** Current filters */
  filters: Filter;
  /** Set filters */
  setFilters: React.Dispatch<React.SetStateAction<Filter>>;
  /** Whether the table is completely empty (no data) */
  isAllEmpty: boolean;
  /** Whether the filtered results are empty (has data but filters exclude all) */
  isFilteredEmpty: boolean;
}

/**
 * Hook for fetching and managing vulnerability data.
 *
 * This hook wraps useGetRisks and adds vulnerability-specific transformations
 * such as source display text formatting based on user type and organization.
 *
 * @param params - Hook configuration parameters
 * @returns Vulnerability data and state management functions
 *
 * @example
 * ```tsx
 * const { data: sourceFilters } = useGetVulnerabilityFilters();
 *
 * const {
 *   data,
 *   isAllEmpty,
 *   filters,
 *   setFilters,
 *   refetch
 * } = useVulnerabilityData({
 *   sourceFilterOptions: sourceFilters?.sourceFilters
 * });
 *
 * // Use transformed data in table
 * <Table
 *   data={data}
 *   status={status}
 *   fetchNextPage={fetchNextPage}
 * />
 * ```
 */
export function useVulnerabilityData({
  parentFilters,
  sourceFilterOptions = [],
  activeColumns,
}: UseVulnerabilityDataParams = {}): UseVulnerabilityDataReturn {
  const { isPraetorianUser, isDemoOrg } = useAuth();

  // Get vulnerability status mode (legacy or new)
  const { mode, isLoading: isModeLoading } = useVulnerabilityStatusMode();

  // Fetch vulnerability data
  const {
    data: rawData,
    error,
    status,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    selectedRows,
    setSelectedRows,
    filters,
    setFilters,
    isAllEmpty,
    isFilteredEmpty,
  } = useGetRisks({ parentFilters, activeColumns });

  // Transform data with display source text
  // Note: No frontend filtering is applied - the backend handles all filtering
  // The frontend simply displays what the API returns (SRP)
  const transformedData = useMemo(() => {
    return rawData.map(risk => ({
      ...risk,
      originalSource: risk.source,
      source: getDisplaySourceText(
        risk,
        isPraetorianUser,
        isDemoOrg,
        AllIntegrations,
        sourceFilterOptions
      ),
    }));
  }, [rawData, isPraetorianUser, isDemoOrg, sourceFilterOptions]);

  return {
    data: transformedData,
    filteredData: transformedData,
    mode,
    isModeLoading,
    error,
    status,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch: async () => {
      await refetch();
    },
    selectedRows,
    setSelectedRows,
    filters,
    setFilters,
    isAllEmpty,
    isFilteredEmpty,
  };
}

/**
 * Gets the original (untransformed) source value from a risk.
 *
 * @param risk - The risk object (potentially with transformed source)
 * @returns The original source value
 *
 * @example
 * ```tsx
 * const originalSource = getOriginalSource(risk);
 * // Use for API calls or comparisons
 * ```
 */
export function getOriginalSource(
  risk: RiskWithVulnerability & { originalSource?: string }
): string | undefined {
  return risk.originalSource ?? risk.source;
}

/**
 * Checks if vulnerability data is loading.
 *
 * @param status - The query status
 * @returns True if data is in loading state
 *
 * @example
 * ```tsx
 * if (isVulnerabilityDataLoading(status)) {
 *   return <Loader />;
 * }
 * ```
 */
export function isVulnerabilityDataLoading(
  status: 'pending' | 'error' | 'success'
): boolean {
  return status === 'pending';
}

/**
 * Checks if vulnerability data has an error.
 *
 * @param status - The query status
 * @returns True if data fetch encountered an error
 *
 * @example
 * ```tsx
 * if (hasVulnerabilityDataError(status)) {
 *   return <ErrorMessage />;
 * }
 * ```
 */
export function hasVulnerabilityDataError(
  status: 'pending' | 'error' | 'success'
): boolean {
  return status === 'error';
}

/**
 * Checks if vulnerability data is successfully loaded.
 *
 * @param status - The query status
 * @returns True if data was successfully fetched
 *
 * @example
 * ```tsx
 * if (isVulnerabilityDataSuccess(status)) {
 *   return <Table data={data} />;
 * }
 * ```
 */
export function isVulnerabilityDataSuccess(
  status: 'pending' | 'error' | 'success'
): boolean {
  return status === 'success';
}
